import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

export const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
};

export const Paywall = () => {
  const { user, billing } = useAuth();
  const isStudent = user?.role === "student";
  const notes = billing?.access?.notes || [];
  return (
    <div className="max-w-2xl mx-auto mt-6 bg-white border border-border rounded-[18px] p-8 sm:p-10 text-center fade-up" data-testid="paywall">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-[#FFF1C7] flex items-center justify-center"><Lock className="h-7 w-7 text-[#7A5A00]" /></div>
      <h2 className="mt-5 text-2xl sm:text-3xl font-display font-semibold">{billing?.trial_available ? "Start your 30-day free trial" : "Your plan isn't active"}</h2>
      <p className="mt-3 text-muted-foreground">
        {isStudent
          ? "Lessons, quizzes and Sage the AI tutor are included when a parent or teacher covers you — or you can start your own plan."
          : "Lessons, quizzes, Sage the AI tutor and AI lesson creation are included in every plan."}
      </p>
      {notes.length > 0 && (
        <ul className="mt-4 text-sm text-[#7A5A00] bg-[#FFF1C7] rounded-lg p-3 text-left space-y-1" data-testid="paywall-notes">
          {notes.map((n) => <li key={n}>• {n}</li>)}
        </ul>
      )}
      {isStudent && billing?.link_code && (
        <div className="mt-6 inline-flex items-center gap-3 border border-border rounded-xl px-4 py-3 bg-[#FBFAF4]">
          <div className="text-left"><div className="text-xs text-muted-foreground">Give this code to your parent</div><div className="font-display text-xl font-semibold tracking-[0.2em]">{billing.link_code}</div></div>
          <Button variant="ghost" size="icon" aria-label="Copy code" onClick={() => { navigator.clipboard?.writeText(billing.link_code); toast.success("Code copied"); }}><Copy className="h-4 w-4" /></Button>
        </div>
      )}
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button asChild className="h-12 px-6" data-testid="paywall-start-trial-button"><Link to="/billing"><Sparkles className="h-4 w-4 mr-2" />{billing?.trial_available ? "Start free trial" : "Choose a plan"}</Link></Button>
        {isStudent && <Button asChild variant="outline" className="h-12 bg-white"><Link to="/dashboard">Join a class with a code</Link></Button>}
      </div>
    </div>
  );
};

export const BillingBanner = () => {
  const { user, billing } = useAuth();
  if (!billing || user?.role === "admin") return null;
  const sub = billing.subscription;
  if (!billing.access?.has_access) {
    return (
      <div className="mb-6 rounded-[14px] border border-[#F2B705]/40 bg-[#FFF1C7] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="billing-banner">
        <div className="flex-1 text-sm text-[#5B4A10]">
          <span className="font-semibold">{billing.trial_available ? "Try everything free for 30 days." : "Your plan isn't active."}</span>{" "}
          {user?.role === "student" ? "Ask a parent to link you, join a teacher's class, or start your own plan." : "Pick a plan to unlock lessons, quizzes and the AI tutor."}
        </div>
        <Button size="sm" asChild data-testid="billing-banner-button"><Link to="/billing">{billing.trial_available ? "Start free trial" : "See plans"}</Link></Button>
      </div>
    );
  }
  if (sub && ["trialing", "active"].includes(sub.status) && user.role !== "student" && sub.seats < billing.seats_used) {
    return (
      <div className="mb-6 rounded-[14px] border border-[#F26B6B]/40 bg-[#FFE1E1] px-4 py-3 flex items-center gap-3 text-sm" data-testid="seats-banner">
        <div className="flex-1 text-[#9B2C2C]">{billing.seats_used - sub.seats} {user.role === "parent" ? "child(ren)" : "student(s)"} not covered by your plan yet.</div>
        <Button size="sm" asChild><Link to="/billing">Add seats</Link></Button>
      </div>
    );
  }
  if (sub?.status === "trialing") {
    const d = daysLeft(sub.trial_end);
    if (d != null && d <= 7) {
      return (
        <div className="mb-6 rounded-[14px] border border-border bg-[#D7F3F1] px-4 py-3 text-sm flex items-center gap-3" data-testid="trial-banner">
          <div className="flex-1">Your free trial ends in {d} day{d === 1 ? "" : "s"}. Your card will be charged automatically after that.</div>
          <Button size="sm" variant="outline" className="bg-white" asChild><Link to="/billing">Manage</Link></Button>
        </div>
      );
    }
  }
  return null;
};
